// src/components/answers/WhiteboardAnswer.js
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { uploadAnswerBlob } from '../../utils/uploadAnswer';

export default function WhiteboardAnswer({
                                             attemptId,
                                             questionId,
                                             onSaved,
                                             disabled = false,
                                             strokeColor = '#111',
                                             strokeWidth = 2.5,
                                         }) {
    const canvasRef = useRef(null);
    const ctxRef = useRef(null);

    // Strokes i undo/redo
    const strokesRef = useRef([]);       // [{color, width, points:[{x,y}...]}]
    const redoStackRef = useRef([]);     // cofnięte strokes
    const drawingRef = useRef(false);
    const activeStrokeRef = useRef(null);
    const activePointerIdRef = useRef(null);

    const [err, setErr] = useState('');
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);

    // UI: status zapisu
    const [uploading, setUploading] = useState(false);
    const [savedAt, setSavedAt] = useState(0);

    const BASE_W = 1000, BASE_H = 700;

    // Redraw całej sceny
    const redrawAll = useMemo(() => {
        return () => {
            const canvas = canvasRef.current;
            const ctx = ctxRef.current;
            if (!canvas || !ctx) return;

            ctx.save();
            ctx.setTransform(1,0,0,1,0,0);
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.restore();

            ctx.save();
            ctx.fillStyle = '#fff';
            ctx.fillRect(0, 0, BASE_W, BASE_H);

            ctx.lineJoin = 'round';
            ctx.lineCap = 'round';

            for (const s of strokesRef.current) {
                ctx.beginPath();
                ctx.strokeStyle = s.color;
                ctx.lineWidth = s.width;
                const pts = s.points;
                if (pts.length === 1) {
                    const p = pts[0];
                    ctx.moveTo(p.x, p.y);
                    ctx.lineTo(p.x + 0.001, p.y + 0.001);
                    ctx.stroke();
                    continue;
                }
                for (let i = 0; i < pts.length - 1; i++) {
                    const a = pts[i], b = pts[i + 1];
                    ctx.moveTo(a.x, a.y);
                    ctx.lineTo(b.x, b.y);
                }
                ctx.stroke();
            }

            if (activeStrokeRef.current) {
                const s = activeStrokeRef.current;
                const pts = s.points;
                ctx.beginPath();
                ctx.strokeStyle = s.color;
                ctx.lineWidth = s.width;
                if (pts.length === 1) {
                    const p = pts[0];
                    ctx.moveTo(p.x, p.y);
                    ctx.lineTo(p.x + 0.001, p.y + 0.001);
                } else {
                    for (let i = 0; i < pts.length - 1; i++) {
                        const a = pts[i], b = pts[i + 1];
                        ctx.moveTo(a.x, a.y);
                        ctx.lineTo(b.x, b.y);
                    }
                }
                ctx.stroke();
            }

            ctx.restore();
        };
    }, []);

    const clientToLogical = (clientX, clientY) => {
        const c = canvasRef.current;
        const r = c.getBoundingClientRect();
        const cssW = r.width || 1;
        const cssH = r.height || 1;
        const xCss = Math.min(Math.max(0, clientX - r.left), cssW);
        const yCss = Math.min(Math.max(0, clientY - r.top), cssH);
        const x = (xCss / cssW) * BASE_W;
        const y = (yCss / cssH) * BASE_H;
        return { x, y };
    };

    const beginStroke = (e) => {
        if (disabled) return;
        if (drawingRef.current) return;
        drawingRef.current = true;
        activePointerIdRef.current = e.pointerId;
        canvasRef.current.setPointerCapture?.(e.pointerId);

        activeStrokeRef.current = {
            color: strokeColor,
            width: (e.pressure && e.pressure > 0 ? strokeWidth * (0.5 + e.pressure) : strokeWidth),
            points: [],
        };

        const { x, y } = clientToLogical(e.clientX, e.clientY);
        activeStrokeRef.current.points.push({ x, y });

        redoStackRef.current = [];
        setCanRedo(false);

        redrawAll();
    };

    const continueStroke = (e) => {
        if (!drawingRef.current) return;
        if (e.pointerId !== activePointerIdRef.current) return;

        const s = activeStrokeRef.current;
        if (!s) return;

        s.width = (e.pressure && e.pressure > 0 ? strokeWidth * (0.5 + e.pressure) : strokeWidth);

        const { x, y } = clientToLogical(e.clientX, e.clientY);
        const last = s.points[s.points.length - 1];
        if (!last || Math.hypot(x - last.x, y - last.y) > 0.7) {
            s.points.push({ x, y });
            redrawAll();
        }
    };

    const endStroke = () => {
        if (!drawingRef.current) return;
        drawingRef.current = false;

        const s = activeStrokeRef.current;
        activeStrokeRef.current = null;
        activePointerIdRef.current = null;

        if (s && s.points.length > 0) {
            strokesRef.current = [...strokesRef.current, s];
            setCanUndo(strokesRef.current.length > 0);
            redrawAll();
        }
    };

    const undo = () => {
        if (!strokesRef.current.length) return;
        const last = strokesRef.current[strokesRef.current.length - 1];
        strokesRef.current = strokesRef.current.slice(0, -1);
        redoStackRef.current = [...redoStackRef.current, last];
        setCanUndo(strokesRef.current.length > 0);
        setCanRedo(redoStackRef.current.length > 0);
        redrawAll();
    };

    const redo = () => {
        if (!redoStackRef.current.length) return;
        const s = redoStackRef.current[redoStackRef.current.length - 1];
        redoStackRef.current = redoStackRef.current.slice(0, -1);
        strokesRef.current = [...strokesRef.current, s];
        setCanUndo(strokesRef.current.length > 0);
        setCanRedo(redoStackRef.current.length > 0);
        redrawAll();
    };

    const clearAll = () => {
        strokesRef.current = [];
        redoStackRef.current = [];
        setCanUndo(false);
        setCanRedo(false);
        redrawAll();
    };

    const savePng = async () => {
        setErr('');
        setUploading(true);
        try {
            const off = document.createElement('canvas');
            off.width = BASE_W; off.height = BASE_H;
            const octx = off.getContext('2d');

            octx.fillStyle = '#fff';
            octx.fillRect(0, 0, BASE_W, BASE_H);

            octx.lineJoin = 'round';
            octx.lineCap = 'round';

            for (const s of strokesRef.current) {
                const pts = s.points;
                octx.beginPath();
                octx.strokeStyle = s.color;
                octx.lineWidth = s.width;
                if (pts.length === 1) {
                    const p = pts[0];
                    octx.moveTo(p.x, p.y);
                    octx.lineTo(p.x + 0.001, p.y + 0.001);
                    octx.stroke();
                    continue;
                }
                for (let i = 0; i < pts.length - 1; i++) {
                    const a = pts[i], b = pts[i + 1];
                    octx.moveTo(a.x, a.y);
                    octx.lineTo(b.x, b.y);
                }
                octx.stroke();
            }

            const blob = await new Promise((res) => off.toBlob(res, 'image/png', 0.92));
            if (!blob) throw new Error('Błąd generowania obrazu');

            const filename = `whiteboard-${questionId}-${Date.now()}.png`;
            const { file_key, meta } = await uploadAnswerBlob({
                attemptId,
                questionId,
                blob,
                filename,
                contentType: 'image/png',
                extraMeta: { kind: 'whiteboard', width: BASE_W, height: BASE_H },
            });

            setSavedAt(Date.now());
            onSaved?.({ file_key, meta });
        } catch (e) {
            setErr(e.message || 'Błąd zapisu rysunku');
        } finally {
            setUploading(false);
        }
    };

    // Znikanie „Zapisano” po 3s
    useEffect(() => {
        if (!savedAt) return;
        const t = setTimeout(() => setSavedAt(0), 3000);
        return () => clearTimeout(t);
    }, [savedAt]);

    // Inicjalizacja i responsywność (bez warningu RO)
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctxRef.current = ctx;

        let rafId = 0;
        const lastCss = { w: 0, h: 0 };

        const fit = () => {
            if (rafId) return;
            rafId = requestAnimationFrame(() => {
                rafId = 0;

                const dpr = window.devicePixelRatio || 1;
                const cssW = Math.max(1, Math.round(canvas.clientWidth));
                const cssH = Math.round(cssW * (BASE_H / BASE_W));

                if (cssW === lastCss.w && cssH === lastCss.h) return;
                lastCss.w = cssW; lastCss.h = cssH;

                canvas.style.height = `${cssH}px`;

                const pxW = Math.max(1, Math.round(cssW * dpr));
                const pxH = Math.max(1, Math.round(cssH * dpr));
                if (canvas.width !== pxW) canvas.width = pxW;
                if (canvas.height !== pxH) canvas.height = pxH;

                ctx.setTransform(pxW / BASE_W, 0, 0, pxH / BASE_H, 0, 0);
                redrawAll();
            });
        };

        const ro = new ResizeObserver(fit);
        ro.observe(canvas);

        const blockCtx = (e) => e.preventDefault();
        canvas.addEventListener('contextmenu', blockCtx);

        lastCss.w = lastCss.h = -1;
        fit();

        return () => {
            ro.disconnect();
            canvas.removeEventListener('contextmenu', blockCtx);
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, [redrawAll]);

    // Pointer Events
    useEffect(() => {
        const c = canvasRef.current;
        if (!c) return;

        const onDown = (e) => { if (disabled) return; e.preventDefault(); beginStroke(e); };
        const onMove = (e) => { if (!drawingRef.current) return; e.preventDefault(); continueStroke(e); };
        const onUpOrCancel = (e) => { e.preventDefault(); endStroke(); c.releasePointerCapture?.(e.pointerId); };

        c.addEventListener('pointerdown', onDown, { passive: false });
        c.addEventListener('pointermove', onMove, { passive: false });
        window.addEventListener('pointerup', onUpOrCancel, { passive: false });
        window.addEventListener('pointercancel', onUpOrCancel, { passive: false });

        return () => {
            c.removeEventListener('pointerdown', onDown);
            c.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUpOrCancel);
            window.removeEventListener('pointercancel', onUpOrCancel);
        };
    }, [disabled, strokeColor, strokeWidth]);

    return (
        <div style={{ display: 'grid', gap: 8, position: 'relative' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'center' }}>
                <div style={{ fontSize: 14, opacity: 0.8,  }}>
                    Rysuj rozwiązanie (mysz / rysik / dotyk).

                </div>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button className={'but-smal'} style={{padding:'5px 10px', fontSize:'12px' }} onClick={undo} disabled={!canUndo || disabled || uploading}>Cofnij</button>
                    <button className={'but-smal'} style={{padding:'5px 10px', fontSize:'12px' }} onClick={redo} disabled={!canRedo || disabled || uploading}>Przywróć</button>
                    <button className={'but-smal'} style={{padding:'5px 10px', fontSize:'12px' }} onClick={clearAll} disabled={disabled || uploading}>Wyczyść</button>
                    <button  className={'but-smal'} style={{fontSize:'20px' }}onClick={savePng} disabled={disabled || uploading}>{uploading ? 'Zapisuję…' : 'Zapisz'}</button>
                </div>
            </div>

            <div style={{ width: '100%', border: '1px solid #ddd', borderRadius: 8, overflow: 'hidden', position: 'relative' }}>
                <canvas
                    ref={canvasRef}
                    width={BASE_W}
                    height={BASE_H}
                    style={{
                        width: '100%',
                        display: 'block',
                        background: '#fff',
                        touchAction: 'none',
                        userSelect: 'auto',
                        WebkitUserSelect: 'auto',
                        cursor: disabled ? 'not-allowed' : 'crosshair',
                    }}
                />


                {/* Znaczek „Zapisano” – pojawia się na 3s po udanym uploadzie */}
                {savedAt > 0 && (
                    <div
                        style={{
                            position: 'absolute',
                            right: 20,
                            bottom: 20,
                            background: '#1f8b4c',
                            color: '#fff',
                            padding: '10px 20px',
                            borderRadius: 10,
                            fontSize: 15,
                            boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                        }}
                    >
                        Zapisano
                    </div>
                )}
            </div>
            <div style={{ fontSize: 18,  color:'red', textAlign: 'right' }}>
                Przed przejściem do następnego pytania Zapisz odpowiedź.
            </div>

            {err && <div style={{ color: '#a00' }}>{err}</div>}
        </div>
    );
}
